import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { consoleHelper } from 'lib/helpers/console-helper.class.js';
import { stringHelper } from 'lib/helpers/string-helper.class.js';
import { firstValueFrom } from 'rxjs';
import { map } from 'rxjs/operators';

export interface IEmbedResult {
	link: string;
	html: string;
}

@Injectable({
	providedIn: 'root'
})
/**
 * Base code taken from https://github.com/SamirHodzic/ngx-embed-video
 * NOTE: This code is modifed beacuse it contained bugs
 */
export class VideoService {
	private readonly validYouTubeOptions = ['default', 'mqdefault', 'hqdefault', 'sddefault', 'maxresdefault'];
	private readonly validVimeoOptions = ['thumbnail_small', 'thumbnail_medium', 'thumbnail_large'];
	private readonly validDailyMotionOptions = [
		'thumbnail_60_url',
		'thumbnail_120_url',
		'thumbnail_180_url',
		'thumbnail_240_url',
		'thumbnail_360_url',
		'thumbnail_480_url',
		'thumbnail_720_url',
		'thumbnail_1080_url'
	];

	public readonly supportedVideoSources: string[] = ['youtube', 'dailymotion', 'vimeo'];

	constructor(private httpClient: HttpClient, private sanitizer: DomSanitizer) {}

	public embed(url: any, options?: any): SafeHtml | undefined {
		let id;

		try {
			url = new URL(url);
		} catch (err) {
			consoleHelper.logToConsole({
				type: 'Warning',
				message: 'Invalid url',
				partA: url
			});

			return;
		}

		if (!options) {
			options = { query: {} };
		}

		id = this.detectYoutube(url);
		if (id) {
			// add 't' param if present in query
			if (url.search) {
				const params = stringHelper.getQueryStringParams(url);
				const t = params.t;

				if (t) {
					options.query = {
						t: t
					};
				}
			}

			return this.embed_youtube(id, options);
		}

		id = this.detectVimeo(url);
		if (id) {
			return this.embed_vimeo(id, options);
		}

		id = this.detectDailymotion(url);
		if (id) {
			return this.embed_dailymotion(id, options);
		}

		return undefined;
	}

	public embed_youtube(id: string, options?: any): SafeHtml {
		options = this.parseOptions(options);

		return this.sanitize_iframe(
			'<iframe src="https://youtube.com/embed/' +
				id +
				options.query +
				'"' +
				options.attr +
				' frameborder="0" allowfullscreen></iframe>'
		);
	}

	public embed_vimeo(id: string, options?: any): SafeHtml {
		options = this.parseOptions(options);

		return this.sanitize_iframe(
			'<iframe src="https://player.vimeo.com/video/' +
				id +
				options.query +
				'"' +
				options.attr +
				' frameborder="0" webkitallowfullscreen mozallowfullscreen allowfullscreen></iframe>'
		);
	}

	public embed_dailymotion(id: string, options?: any): SafeHtml {
		options = this.parseOptions(options);

		return this.sanitize_iframe(
			'<iframe src="https://www.dailymotion.com/embed/video/' +
				id +
				options.query +
				'"' +
				options.attr +
				' frameborder="0" allowfullscreen></iframe>'
		);
	}

	public embed_image(url: any, options?: any): Promise<IEmbedResult | undefined> | undefined {
		let id: string | undefined;

		try {
			url = new URL(url);
		} catch (err) {
			consoleHelper.logToConsole({
				type: 'Warning',
				message: 'Invalid url',
				partA: url
			});
			return undefined;
		}

		id = this.detectYoutube(url);
		if (id) {
			return this.embed_youtube_image(id, options);
		}

		id = this.detectVimeo(url);
		if (id) {
			return this.embed_vimeo_image(id, options);
		}

		id = this.detectDailymotion(url);
		if (id) {
			return this.embed_dailymotion_image(id, options);
		}

		return undefined;
	}

	private embed_youtube_image(id: string, options?: any): Promise<IEmbedResult | undefined> {
		if (typeof options === 'function') {
			options = {};
		}
		options = options || {};

		options.image = this.validYouTubeOptions.indexOf(options.image) > 0 ? options.image : 'default';

		const src = 'https://img.youtube.com/vi/' + id + '/' + options.image + '.jpg';

		const result = {
			link: src,
			html: '<img src="' + src + '"/>'
		};

		return new Promise((resolve, reject) => {
			resolve(result);
		});
	}

	private embed_vimeo_image(id: string, options?: any): Promise<IEmbedResult | undefined> {
		if (typeof options === 'function') {
			options = {};
		}

		options = options || {};

		options.image = this.validVimeoOptions.indexOf(options.image) >= 0 ? options.image : 'thumbnail_large';

		return firstValueFrom(
			this.httpClient.get<any>('https://vimeo.com/api/v2/video/' + id + '.json').pipe(
				map((res) => {
					return {
						link: res.json()[0][options.image],
						html: '<img src="' + res.json()[0][options.image] + '"/>'
					};
				})
			)
		);
	}

	private embed_dailymotion_image(id: string, options?: any): Promise<IEmbedResult | undefined> {
		if (typeof options === 'function') {
			options = {};
		}

		options = options || {};

		options.image = this.validDailyMotionOptions.indexOf(options.image) >= 0 ? options.image : 'thumbnail_480_url';

		return firstValueFrom(
			this.httpClient.get<any>('https://api.dailymotion.com/video/' + id + '?fields=' + options.image).pipe(
				map((res) => {
					return {
						link: res.json()[options.image],
						html: '<img src="' + res.json()[options.image] + '"/>'
					};
				})
			)
		);
	}

	private parseOptions(options: any): {
		query: string;
		attr: string;
	} {
		let queryString = '';
		let attributes = '';

		if (options && options?.['query']) {
			queryString = '?' + this.serializeQuery(options.query);
		}

		if (options && options?.['attr']) {
			const temp = <any>[];

			if (options.attr) {
				for (const key of Object.keys(options.attr)) {
					temp.push(key + '="' + options.attr[key] + '"');
				}
			}

			attributes = ' ' + temp.join(' ');
		}

		return {
			query: queryString,
			attr: attributes
		};
	}

	private serializeQuery(query: any): string {
		const queryString: any = [];

		if (!query) {
			return '';
		}

		for (const key of Object.keys(query)) {
			// key: the name of the object key
			// index: the ordinal position of the key within the object
			const value = query[key];
			queryString.push(key + '=' + value);
		}

		return queryString.join('&');
	}

	private sanitize_iframe(iframe: string): SafeHtml {
		return this.sanitizer.bypassSecurityTrustHtml(iframe);
	}

	private detectVimeo(url: URL): string | undefined {
		try {
			return url.hostname === 'vimeo.com' ? url.pathname.split('/')[1] : undefined;
		} catch (err) {
			return undefined;
		}
	}

	private detectYoutube(url: URL): string | undefined {
		try {
			if (url.hostname.indexOf('youtube.com') > -1) {
				if (url.pathname.startsWith('/shorts')) {
					// handle youtube shorts
					const pathAsArray = url.pathname.split('/');

					if (pathAsArray.length >= 2) {
						return pathAsArray[2];
					}
				}

				const params = stringHelper.getQueryStringParams(url);
				const vParam = params.v;

				if (vParam) {
					return vParam.toString();
				}

				return undefined;
			}

			if (url.hostname === 'youtu.be') {
				return url.pathname.split('/')[1];
			}
		} catch (err) {
			return undefined;
		}
		return undefined;
	}

	private detectDailymotion(url: URL): string | undefined {
		try {
			if (url.hostname.indexOf('dailymotion.com') > -1) {
				return url.pathname.split('/')[2].split('_')[0];
			}

			if (url.hostname === 'dai.ly') {
				return url.pathname.split('/')[1];
			}
		} catch (err) {
			return undefined;
		}

		return undefined;
	}
}
